import { useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { AppShell } from "../components/AppShell";
import { ErrorState, LoadingBlock } from "../components/Feedback";
import { GateChip, ProgressBar, moduleGates } from "../components/Gates";
import { Icon } from "../components/Icon";
import { api } from "../lib/api";
import { useSession } from "../lib/session";
import { useAsync } from "../lib/useAsync";
import { formatMinutes, pluralise } from "../lib/format";
import type { CourseResponse, ModuleSummary, Progress } from "../lib/types";
import NotFound from "./NotFound";

interface NextStep {
  module: ModuleSummary;
  kind: "lesson" | "quiz" | "lab";
  title: string;
  href: string;
}

/** Where a learner should pick up: the first unfinished gate, in course order. */
function findNextStep(course: CourseResponse): NextStep | null {
  const modules = course.levels.flatMap((level) => level.modules).filter(Boolean);
  for (const module of modules) {
    const progress = course.progress.modules[module.id];
    if (progress?.complete) continue;

    const firstUndone = module.lessons.find(
      (_lesson, index) => index >= (progress?.lessonsDone ?? 0),
    );
    if (firstUndone) {
      return {
        module,
        kind: "lesson",
        title: firstUndone.title,
        href: `/modules/${module.id}/lessons/${firstUndone.id}`,
      };
    }
    if (module.quizId && !progress?.quizPassed) {
      return { module, kind: "quiz", title: "Module quiz", href: `/modules/${module.id}/quiz` };
    }
    if (module.labId && !progress?.labPassed) {
      return {
        module,
        kind: "lab",
        title: module.labTitle ?? "Lab",
        href: `/modules/${module.id}/lab`,
      };
    }
  }
  return null;
}

function levelCounts(level: { modules: ModuleSummary[] }, progress: Progress) {
  const total = level.modules.length;
  const complete = level.modules.filter((m) => progress.modules[m.id]?.complete).length;
  return { total, complete };
}

export default function CourseMap() {
  const { courseId = "" } = useParams();
  const { setProgress } = useSession();
  const { data, loading, error, reload } = useAsync((signal) => api.course(courseId, signal), [courseId]);

  useEffect(() => {
    if (data?.progress) setProgress(data.progress);
  }, [data, setProgress]);

  const next = useMemo(() => (data ? findNextStep(data) : null), [data]);
  const certificateHref = `/courses/${courseId}/certificate`;

  if (error && !data && typeof error === "object" && "status" in error && (error as { status: number }).status === 404) {
    return <NotFound />;
  }

  return (
    <AppShell>
      {loading && !data ? (
        <LoadingBlock label="Loading the course map" />
      ) : error && !data ? (
        <ErrorState error={error} onRetry={() => void reload()} title="The course map did not load" />
      ) : data ? (
        <>
          <nav className="crumbs" aria-label="Breadcrumb">
            <Link to="/">Courses</Link>
            <span aria-hidden="true"> / </span>
            <span aria-current="page">{data.course.title}</span>
          </nav>
          <section className="resume" aria-labelledby="resume-heading">
            <div>
              <p className="resume-kind" id="resume-heading">
                {next
                  ? next.kind === "lesson"
                    ? "Next lesson"
                    : next.kind === "quiz"
                      ? "Next up — the quiz"
                      : "Next up — the lab"
                  : "Course complete"}
              </p>
              <p className="resume-address">
                {next ? next.module.id : `all ${data.progress.overall.modulesTotal} modules`}
              </p>
              <h2>{next ? next.title : "Every module is done. Claim your certificate."}</h2>
            </div>
            <div className="resume-action">
              {next ? (
                <>
                  <Link className="btn btn-primary" to={next.href}>
                    {next.kind === "lesson"
                      ? "Continue reading"
                      : next.kind === "quiz"
                        ? "Take the quiz"
                        : "Open the lab"}
                  </Link>
                  <Link className="btn btn-quiet btn-sm" to={`/modules/${next.module.id}`}>
                    Module {String(next.module.number).padStart(2, "0")} overview
                  </Link>
                </>
              ) : (
                <Link className="btn btn-primary" to={certificateHref}>
                  <Icon name="award" />
                  Go to your certificate
                </Link>
              )}
            </div>
          </section>

          <section className="overall" aria-label="Overall progress">
            <div className="overall-line">
              <span>
                <strong>{data.progress.overall.modulesComplete}</strong> of{" "}
                <strong>{data.progress.overall.modulesTotal}</strong> modules complete
              </span>
              <span>
                {data.progress.certificate ? (
                  <Link to={certificateHref}>
                    Certificate {data.progress.certificate.id} issued
                  </Link>
                ) : (
                  <span className="dim">
                    Certificate unlocks when every module is complete
                  </span>
                )}
              </span>
            </div>
            <ProgressBar
              percent={data.progress.overall.percent}
              label={`${data.progress.overall.percent}% of ${data.course.title} complete`}
              done={data.progress.overall.percent === 100}
            />
          </section>

          {data.levels.map((level) => {
            const counts = levelCounts(level, data.progress);
            return (
              <section className="level" key={level.id} aria-labelledby={`level-${level.id}`}>
                <div className="level-head">
                  <h2 id={`level-${level.id}`}>{level.title}</h2>
                  <span className="level-count">
                    {counts.complete}/{counts.total} complete
                  </span>
                </div>
                <div className="level-rail">
                  {level.modules.map((module) => {
                    const progress = data.progress.modules[module.id];
                    const gates = moduleGates(module, progress);
                    return (
                      <article
                        className={progress?.complete ? "module-row is-complete" : "module-row"}
                        key={module.id}
                      >
                        <p className="module-number" aria-hidden="true">
                          {String(module.number).padStart(2, "0")}
                        </p>
                        <div className="module-main">
                          <h3>
                            <Link to={`/modules/${module.id}`}>
                              <span className="sr-only">
                                Module {module.number}:{" "}
                              </span>
                              {module.title}
                            </Link>
                          </h3>
                          <p className="module-summary">{module.summary}</p>
                          <div className="gates">
                            {gates.map((gate) => (
                              <GateChip key={gate.key} state={gate.state} label={gate.label} />
                            ))}
                          </div>
                        </div>
                        <div className="module-side">
                          {module.capstone ? (
                            <span className="capstone-flag">Capstone</span>
                          ) : null}
                          <span className="est">
                            {formatMinutes(module.estimatedMinutes)}
                          </span>
                          <span className="est">
                            {pluralise(module.lessons.length, "lesson")}
                          </span>
                          <Link className="btn btn-sm" to={`/modules/${module.id}`}>
                            {progress?.complete
                              ? "Review"
                              : (progress?.lessonsDone ?? 0) > 0
                                ? "Continue"
                                : "Start"}
                          </Link>
                        </div>
                      </article>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </>
      ) : null}
    </AppShell>
  );
}
